/**
 * How far the DK sportsbook bottom tab bar overlaps the Squares iframe.
 *
 * Squares runs embedded, so `env(safe-area-inset-bottom)` is 0 here and is NOT the
 * right lever; this offset is. The iframe runs to (or very near) the bottom of the
 * device screen and DK's tab bar is painted over its last stretch, so anything
 * bottom-pinned has to be lifted clear of that overlap or it is silently cut in half.
 *
 * ### Why 36 (the value in `src/index.css`)
 *
 * The overlap is ~30px. Two independent device captures agree, and it is worth
 * recording both, because reading either one alone got this wrong twice:
 *
 *   - Anchored build, offset 92px: the banner's bottom edge sat ~62px above the top
 *     of the tab bar. Overlap = 92 − 62 ≈ 30.
 *   - Pre-anchor build, where the banner's bottom edge was flush with the iframe's
 *     by construction: a 320x100 creative came up ~33px short of fully visible.
 *
 * The trap is that the second capture *looks* like proof the offset must be large —
 * the ad is plainly cut off — when it is really proof the iframe extends ~30px past
 * the bar. Only compare captures from a build whose anchoring you have confirmed.
 *
 * 36px is that 30 plus a few px of headroom, because the asymmetry still holds: a
 * hair of gap is cosmetic, whereas short clips a paid creative and costs viewability.
 * Do not retune this from a screenshot without first confirming which build it came
 * from and where the banner is anchored in it.
 *
 * ### Where the number lives
 *
 * ⚠️ Not here. This module has no tunable constant — deliberately. The value is the
 * `--squares-embed-bottom-chrome` declaration in `src/index.css`, and that is the only
 * place to change it.
 *
 * It has to be CSS, because the value is media-query dependent: desktop/standalone is
 * not inside DK's tab bar and must clear 0, which no compiled constant can express.
 *
 * There used to be a second copy here, `EMBED_BOTTOM_CHROME_PX`, wired in as the
 * `var()` fallback below. That made it look like the knob while being unreachable —
 * a fallback only applies when the var is undefined, and the stylesheet always defines
 * it. #759 ("set EMBED_BOTTOM_CHROME_PX to 0") therefore shipped to production as a
 * no-op: the constant read 0, `index.css` still read 36px, and 36px is what rendered.
 * A number that cannot move anything is worse than no number, so it is gone.
 *
 * Resolution order at runtime:
 *
 *   1. `?bottomInset=NN` on the embed URL — the embedder knows the real number, and
 *      this is the contract to ask DK for. See `applyEmbedBottomChromeOverride`.
 *   2. The `--squares-embed-bottom-chrome` CSS var in `src/index.css`.
 *
 * Every bottom-pinned element in Squares (rules popup panel, anchored footer ad
 * banner) is measured from the same origin, so they must all clear the chrome the
 * same way. Honour it exactly once per pinned stack — by whichever element sits
 * closest to the viewport bottom — and have everything above it add the same inset
 * on top of its own height, rather than mixing var-based positions with px-only
 * reserved offsets.
 */

/**
 * Class that marks the document as running inside the DK native app's webview.
 *
 * The chrome value hangs off this in `index.css` rather than off a viewport width. See
 * `isDkNativeEmbed` for why the context has to be sniffed, and `applyDkNativeEmbedClass`
 * for when it is applied.
 */
export const DK_NATIVE_EMBED_CLASS = 'dk-native-embed';

/**
 * Test/harness override forcing the native-embed context on.
 *
 * `public/dk-shell.html` exists to simulate the native container, but it runs in a
 * desktop or mobile *browser*, whose user agent correctly says "browser" — so without
 * this the harness would reproduce the web case and never the one it was built for.
 * Same dev-affordance role as `?gamMock=1`.
 */
export const DK_NATIVE_EMBED_PARAM = 'dkNativeEmbed';

/**
 * Whether this document is running inside DK's native app rather than a web browser.
 *
 * ### Why this cannot be measured, only sniffed
 *
 * The two contexts are identical from inside the frame. Both are iframes, so
 * `window.self !== window.top` is true in each; neither receives `safe-area-inset`
 * (insets go to the top-level document, which is why the harness notes it reads 0 in the
 * embed); and viewport width says nothing, since a tablet in the app and a phone on the
 * web overlap in size. The user agent is the only signal there is.
 *
 * ### The two standard tells
 *
 *   - **Android**: an in-app `WebView` puts `; wv` in the UA. Chrome does not.
 *   - **iOS**: `WKWebView` omits the `Safari/` token that Mobile Safari, Chrome (CriOS)
 *     and Firefox (FxiOS) all carry.
 *
 * ### What it gets wrong, and why that is the right way round
 *
 * Other apps' in-app browsers — Facebook, Instagram — look like WKWebView too and will
 * be treated as native, reserving ~100px they do not need. That is a dark band at the
 * bottom of a web page: visible, harmless, and reversible by the embedder sending
 * `?bottomInset=0`. The opposite mistake — classifying DK's app as web — reserves
 * nothing and puts a paid creative entirely underneath the tab bar, which is the defect
 * this whole line of work exists to fix. Anything unrecognised therefore falls to the
 * web default of 0 only when it is clearly a browser.
 *
 * Reported on every Sentry event as `embed.context`, so misclassification is visible in
 * prod rather than theoretical.
 */
export const isDkNativeEmbed = (
  userAgent: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
  search: string = typeof window === 'undefined' ? '' : window.location.search
): boolean => {
  if (new URLSearchParams(search).get(DK_NATIVE_EMBED_PARAM) === '1') return true;

  if (/Android/.test(userAgent)) return /;\s*wv\b/.test(userAgent);
  if (/iPhone|iPad|iPod/.test(userAgent)) return !/Safari\//.test(userAgent);

  return false;
};

/** Which mobile platform the document is running on, as far as the UA admits. */
export type EmbedPlatform = 'ios' | 'android' | 'unknown';

/** Classes carrying the platform, so the stylesheet can hold a value per platform. */
export const EMBED_PLATFORM_CLASS: Record<Exclude<EmbedPlatform, 'unknown'>, string> = {
  ios: 'dk-embed-ios',
  android: 'dk-embed-android',
};

/**
 * The platform, from the user agent.
 *
 * Needed because the tab-bar overlap is not one number. `prod-v1.5.175` shipped a
 * single 100px to both platforms and collected both complaints at once, pointing
 * opposite ways — "too high on android" and "still cut off" on iOS. The clearance is
 * therefore per platform, and this is what picks the row.
 *
 * Cheap and deliberately coarse: it only has to be right enough to select a CSS class,
 * and `?bottomInset` overrides whatever it picks.
 */
export const embedPlatform = (
  userAgent: string = typeof navigator === 'undefined' ? '' : navigator.userAgent
): EmbedPlatform => {
  if (/Android/.test(userAgent)) return 'android';
  if (/iPhone|iPad|iPod/.test(userAgent)) return 'ios';
  return 'unknown';
};

/**
 * Mark the document with its embed context and platform, if it is a native embed.
 *
 * Call before `applyEmbedBottomChromeOverride` and before first paint: the classes only
 * select which stylesheet value wins, so an embedder-supplied `?bottomInset` set as an
 * inline style afterwards still overrides them.
 *
 * The platform class goes on only alongside the native one. On the web the clearance is
 * zero whatever the device, and tagging a plain browser with a platform would invite a
 * future rule that reserves chrome where there is none.
 */
export const applyDkNativeEmbedClass = (): boolean => {
  if (typeof document === 'undefined') return false;

  const native = isDkNativeEmbed();
  const platform = embedPlatform();
  const { classList } = document.documentElement;

  classList.toggle(DK_NATIVE_EMBED_CLASS, native);
  classList.toggle(EMBED_PLATFORM_CLASS.ios, native && platform === 'ios');
  classList.toggle(EMBED_PLATFORM_CLASS.android, native && platform === 'android');

  return native;
};

/** CSS custom property every bottom-pinned element resolves its offset through. */
export const EMBED_BOTTOM_CHROME_VAR = '--squares-embed-bottom-chrome';

/**
 * Last-resort length used only if `index.css` never loaded — at which point the app
 * is unstyled and the ad banner is the least of it. NOT the tuning knob: raising it
 * moves nothing while the stylesheet defines the var, which is exactly the trap that
 * made #759 a no-op. Tune `--squares-embed-bottom-chrome` in `src/index.css`.
 *
 * `0px` rather than a guess at the overlap, because a bare `var()` with no fallback
 * resolves to nothing and turns every `calc()` here into an invalid declaration —
 * which drops the offset entirely rather than degrading it.
 */
const STYLESHEET_MISSING_FALLBACK = '0px';

/** CSS length for the embed chrome. Resolves through the var; see above. */
export const EMBED_BOTTOM_CHROME = `var(${EMBED_BOTTOM_CHROME_VAR}, ${STYLESHEET_MISSING_FALLBACK})`;

/**
 * The chrome's live value in px, read back from the cascade.
 *
 * The only honest way to get a number out of this module: it resolves whichever of
 * the `?bottomInset` override, the media-query-dependent stylesheet value, or the
 * fallback actually won. Returns null when it cannot be resolved (no DOM, or a value
 * that is not a px length).
 */
export const readEmbedBottomChromePx = (): number | null => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;

  const raw = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue(EMBED_BOTTOM_CHROME_VAR)
    .trim();

  const value = Number.parseFloat(raw);
  return raw.endsWith('px') && Number.isFinite(value) ? value : null;
};

/**
 * Custom property mirroring `env(safe-area-inset-bottom)`, declared in `index.css`.
 *
 * `env()` cannot be read back from `getComputedStyle`, so the stylesheet copies it into
 * a var we can. See `readSafeAreaInsetBottomPx`.
 */
export const SAFE_AREA_INSET_BOTTOM_VAR = '--squares-safe-area-inset-bottom';

/**
 * What the platform reports as obscured at the bottom of the viewport, in px.
 *
 * Diagnostic only — deliberately not wired into layout. It covers *system* UI (on iOS,
 * the home indicator) and never DK's own tab bar, so it can only ever be a component of
 * the overlap, never the whole of it. Its value is in testing the claim this module has
 * asserted untested since #699: that it is 0 inside the embed. A non-zero reading would
 * be the first evidence from inside the webview that our frame really does extend into
 * territory the platform considers covered.
 */
export const readSafeAreaInsetBottomPx = (): number | null => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;

  const raw = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue(SAFE_AREA_INSET_BOTTOM_VAR)
    .trim();

  const value = Number.parseFloat(raw);
  return raw.endsWith('px') && Number.isFinite(value) ? value : null;
};

/** Embed URL parameter carrying the parent app's real bottom overlap, in CSS px. */
export const EMBED_BOTTOM_CHROME_PARAM = 'bottomInset';

/**
 * Upper bound on an accepted override. A value larger than this is a mistake (or a
 * tampered URL) rather than a tab bar, and honouring it would push the anchored ad
 * off the top of a phone.
 */
export const EMBED_BOTTOM_CHROME_MAX_PX = 240;

/**
 * Parse a bottom-inset override out of a query string. Returns null when absent or
 * not a finite number within range — a bad value must leave the default standing
 * rather than collapse the offset to 0 and clip the ad.
 */
export const readEmbedBottomChromeOverride = (search: string): number | null => {
  const raw = new URLSearchParams(search).get(EMBED_BOTTOM_CHROME_PARAM);
  if (raw === null || raw.trim() === '') return null;

  const value = Number(raw);
  if (!Number.isFinite(value) || value < 0 || value > EMBED_BOTTOM_CHROME_MAX_PX) return null;

  return Math.round(value);
};

/**
 * How the bottom inset resolved on this page load — for reporting, not for layout.
 *
 * The open question this answers is whether DK's native app implements the
 * `?bottomInset=NN` contract at all. That has three distinguishable outcomes, and a
 * single resolved number cannot tell them apart:
 *
 *   - absent  — DK never sent one, so the stylesheet value is doing the work and the
 *               contract is still unimplemented.
 *   - rejected — DK sent something we refused (non-numeric, negative, or over
 *               EMBED_BOTTOM_CHROME_MAX_PX). This is the one that would otherwise look
 *               identical to "absent" while actually meaning DK HAS shipped it and we
 *               are throwing their value away.
 *   - applied — DK sent a usable number and it won.
 *
 * `resolvedPx` is what the cascade actually ended up with, so an applied override that
 * somehow failed to take effect is visible too.
 */
export type EmbedBottomChromeSource = 'applied' | 'rejected' | 'absent';

export interface EmbedBottomChromeReport {
  source: EmbedBottomChromeSource;
  /** Exactly what the embedder put in the URL, untouched. Null when absent. */
  paramRaw: string | null;
  /** The accepted override, or null when absent/rejected. */
  appliedPx: number | null;
  /** The live value from the cascade — override, stylesheet, or fallback. */
  resolvedPx: number | null;
  /**
   * `env(safe-area-inset-bottom)` as the platform reports it. Diagnostic — see
   * `readSafeAreaInsetBottomPx`. Null when it cannot be resolved.
   */
  safeAreaInsetBottomPx: number | null;
  /**
   * Which context the chrome value was chosen for. The whole reserve hangs off this, so
   * it is reported to make a misclassification visible rather than mysterious.
   */
  embedContext: 'dk-native' | 'browser';
  /**
   * The platform the clearance was chosen for. Reported alongside the context because
   * the value is now per platform, so "which row did this session get" is two fields.
   */
  embedPlatform: EmbedPlatform;
}

export const describeEmbedBottomChrome = (
  search: string = typeof window === 'undefined' ? '' : window.location.search
): EmbedBottomChromeReport => {
  const raw = new URLSearchParams(search).get(EMBED_BOTTOM_CHROME_PARAM);
  const applied = readEmbedBottomChromeOverride(search);
  const present = raw !== null && raw.trim() !== '';

  return {
    source: applied !== null ? 'applied' : present ? 'rejected' : 'absent',
    paramRaw: raw,
    appliedPx: applied,
    resolvedPx: readEmbedBottomChromePx(),
    safeAreaInsetBottomPx: readSafeAreaInsetBottomPx(),
    embedContext: isDkNativeEmbed(undefined, search) ? 'dk-native' : 'browser',
    embedPlatform: embedPlatform(),
  };
};

/**
 * Apply an embed-supplied bottom inset, if the URL carries one.
 *
 * Called once at boot, before first paint, so pinned elements never position against
 * the default and then jump. Writing the var on the root element means every consumer
 * — CSS and the TS length helpers alike — picks it up with no further plumbing.
 */
export const applyEmbedBottomChromeOverride = (
  search: string = typeof window === 'undefined' ? '' : window.location.search
): number | null => {
  const override = readEmbedBottomChromeOverride(search);
  if (override === null || typeof document === 'undefined') return null;

  document.documentElement.style.setProperty(EMBED_BOTTOM_CHROME_VAR, `${override}px`);
  return override;
};

/**
 * Bottom-anchored length that also clears the embed chrome.
 *
 * Takes a raw pixel count or any CSS length — including a `var(...)` a component
 * publishes its measured height into — so a reserved space that tracks a live
 * element still counts the chrome exactly once, here.
 */
export const withEmbedBottomChrome = (length: number | string) =>
  `calc(${typeof length === 'number' ? `${length}px` : length} + ${EMBED_BOTTOM_CHROME})`;
